import { useState } from "react";
import axios from "axios";
import {
  Container,
  Stack,
  Button,
  useMediaQuery,
  useTheme,
  Checkbox,
  FormControl,
  FormHelperText,
  Modal,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { Text } from "../components/Text";
import { FormInput } from "../components/FormInput";
import { FormRadioButtons } from "../components/FormRadioButtons";
import { TextKv } from "../components/TextKv";
import { Parentheses } from "../components/Parentheses";

const contactTypes = [
  "ベクター販売について / About vector Subject",
  "共同研究の詳細・実績について / Inquiries about details and achievements of collaborative research",
  "受託研究のご相談について / Inquiry about research contracts",
  "ベクター設計・構築の仕様やカスタム設計について / Vector design and custom construction specifications",
];

export const Contact = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type") ?? "1";

  const [mailSend, setMailSend] = useState<boolean>(false);

  const {
    handleSubmit,
    control,
    formState: { isValid, errors },
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      company: "",
      email: "",
      type: type,
      message: "",
      consent: false,
    },
  });

  const onSubmit = async (data: any) => {
    const emailText = `${
      data.name
    }様\n\nこの度は、お問い合わせいただきありがとうございます。\n現在、担当者が確認を行っており、できるだけ早急にご対応させていただきます。\n少々お待ちいただけますようお願い申し上げます。\n\n何かご不明な点がございましたら、お気軽にお知らせください。\n\n今後ともよろしくお願い申し上げます。\n\n敬具\n\n\n氏名：${
      data.name
    }\n会社名、大学名、研究室名：${data.company}\nお問い合わせ項目：${
      contactTypes[Number(data.type) - 1]
    }\nお問い合わせ内容：\n${data.message}\n`;

    const emailData = {
      to: data.email,
      subject: "お問い合わせいただきありがとうございます",
      message: emailText,
      apiKey: process.env.REACT_APP_EMAIL_API_KEY,
    };

    const API_URL = process.env.REACT_APP_EMAIL_POST_URL ?? "";

    try {
      await axios.post(API_URL, emailData, {
        headers: {
          Authorization: `Bearer ${emailData.apiKey}`,
        },
      });
      setMailSend(true);
      reset();
    } catch (error: any) {
      console.error(
        "Error sending email:",
        error.response ? error.response.data : error.message
      );
    }
  };

  return (
    <Stack>
      <TextKv text="お問い合わせ" en="Contact Us" />
      <Container>
        <Stack
          sx={{
            boxShadow: "0px 0px 8px 2px rgba(221, 221, 221, 0.4)",
            borderRadius: "40px",
            padding: isMobile ? "30px 20px" : "60px 110px",
          }}
        >
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <FormInput
              label="氏名 / Full Name"
              name="name"
              marginTop={0}
              control={control}
              errors={errors}
            />
            <FormInput
              label="会社名、大学名、研究室名など / Affiliation"
              name="company"
              control={control}
              errors={errors}
            />
            <FormInput
              label="メールアドレス / E-mail address"
              name="email"
              control={control}
              errors={errors}
            />
            <FormRadioButtons
              labels={contactTypes}
              title="お問い合わせ項目 / Inquiry Item"
              name="type"
              control={control}
            />
            <FormInput
              label="お問い合わせ内容 / Details of your inquiry"
              name="message"
              control={control}
              errors={errors}
            />
            <Text textAlign="right" color="#7e0000" fontWeight={700}>
              ＊ : 必須 / indispensable
            </Text>
            <Stack
              position="relative"
              direction="row"
              justifyContent="center"
              alignItems="center"
              marginTop={isMobile ? 2 : 4}
            >
              <Controller
                name="consent"
                control={control}
                rules={{
                  required: "プライバシーポリシーの同意は必須です",
                }}
                render={({ field }) => (
                  <FormControl error={!!errors.consent}>
                    <Checkbox
                      {...field}
                      size="small"
                      sx={{
                        width: 30,
                        height: 30,
                      }}
                    />
                  </FormControl>
                )}
              />
              <Link
                to="/privacy"
                style={{
                  color: "#006400",
                  textDecoration: "underline",
                }}
              >
                プライバシーポリシー
              </Link>
              に同意する / I agree to the privacy policy
            </Stack>
            {errors.consent && (
              <FormHelperText
                sx={{
                  textAlign: "center",
                  color: "#d32f2f",
                }}
              >
                {errors.consent.message}
              </FormHelperText>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!isValid}
              sx={{
                position: "relative",
                display: "block",
                margin: "36px auto",
                width: 260,
              }}
            >
              送信 / Submit
              <img
                src="/images/btn-arr.png"
                alt=""
                style={{
                  position: "absolute",
                  top: "50%",
                  right: isMobile ? "20px" : "45px",
                  transform: "translateY(-50%)",
                }}
              />
            </Button>
          </form>
        </Stack>
      </Container>
      <Modal open={mailSend} onClose={() => setMailSend(false)}>
        <Stack
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 800,
            background: "#fff",
            boxShadow: 24,
            p: 4,
          }}
        >
          <Stack width={100} margin="0 auto 10px">
            <img src="/images/kv-logo.png" alt="" />
          </Stack>
          <Parentheses>
            <Stack textAlign="center">
              <Text color="primary" fontSize={20} fontWeight="bold">
                お問い合わせありがとうございます！
              </Text>
              <Text marginTop={1}>
                担当者が確認次第、<b>2営業日以内に返信させていただきます</b>
                ので、少々お待ちください。
                <br />
                何かご不明点がございましたら、再度ご連絡いただければと思います。
                <br />
                今後ともよろしくお願い申し上げます。
              </Text>
            </Stack>
          </Parentheses>
        </Stack>
      </Modal>
    </Stack>
  );
};
